/** 알림 목록 · 읽음(`W-60`). 헤더 벨의 점과 패널이 **같은 질문 하나**를 나눠 쓴다.
 *
 * ## 비용 (다른 화면에 얹지 않는다)
 *
 * - 키는 `["notifications"]` 하나다. ⛔ `["jobs", …]` 아래에 두지 않는다. 작업 화면들이 `["jobs"]` 를
 *   무효화할 때마다 이 질문까지 다시 나가면 알림과 무관한 편집 하나가 요청을 하나 더 낸다.
 * - 벨(`Shell.tsx`)은 창이 보일 때만 60초 간격으로 묻는다(`refetchIntervalInBackground` 기본값 `false`).
 *   응답은 D1 두 문장 + 파생 칸이 있을 때만 두 문장이다(`apps/api/src/lib/notifications.ts`).
 * - 읽음 요청은 목록을 다시 받지 않고 캐시를 고친다. 단건 응답이 그 줄과 안 읽음 수를 함께 준다.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  notificationEndpoints,
  type NotificationListView,
  type NotificationReadAllResponse,
  type NotificationReadResponse,
} from "@makit/shared";

import { request } from "../../lib/api";

export const NOTIFICATIONS_QUERY_KEY = ["notifications"] as const;

/** 벨 점을 갱신하는 간격. 창이 가려져 있으면 묻지 않는다. */
export const NOTIFICATIONS_POLL_MS = 60_000;

export function fetchNotifications(): Promise<NotificationListView> {
  return request<NotificationListView>(notificationEndpoints.list());
}

export function readNotification(id: string): Promise<NotificationReadResponse> {
  return request<NotificationReadResponse>(notificationEndpoints.read(id), { method: "POST" });
}

export function readAllNotifications(): Promise<NotificationReadAllResponse> {
  return request<NotificationReadAllResponse>(notificationEndpoints.readAll(), { method: "POST" });
}

/** 목록 질문. `poll` 은 벨만 켠다. 패널은 같은 키를 읽어 열 때 낡았으면 새로 묻는다. */
export function useNotifications(options: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: fetchNotifications,
    ...(options.poll ? { refetchInterval: NOTIFICATIONS_POLL_MS } : {}),
  });
}

/** 단건 · 모두 읽음. **실패해도 화면을 막지 않는다**(PRD `7.6.12` ⓔ). 점이 남아 있을 뿐이고 다음 목록이
 *  바로잡는다. */
export function useNotificationReads() {
  const queryClient = useQueryClient();

  const readOne = useMutation({
    mutationFn: readNotification,
    onSuccess: (res) => {
      queryClient.setQueryData<NotificationListView>(NOTIFICATIONS_QUERY_KEY, (prev) =>
        prev
          ? {
              items: prev.items.map((item) => (item.id === res.notification.id ? res.notification : item)),
              unreadCount: res.unreadCount,
            }
          : prev,
      );
    },
  });

  const readAll = useMutation({
    mutationFn: readAllNotifications,
    // 서버가 박은 `readAt` 을 지어내지 않는다. 다시 묻는다.
    onSettled: () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
  });

  return { readOne, readAll };
}
